import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  CreditCard, Save, Loader2, Edit, Trash2, Plus, Check, X,
  Star, Crown, Building2, Sparkles, ChevronDown, ChevronUp, Globe, MapPin, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES_LIST = [
  { key: 'has_tutor_ia', label: 'Tutor IA', icon: '🤖' },
  { key: 'has_simulacro', label: 'Simulacros', icon: '📝' },
  { key: 'has_quick_test', label: 'Tests Rápidos', icon: '⚡' },
  { key: 'has_flashcards', label: 'Flashcards', icon: '🃏' },
  { key: 'has_study_plan', label: 'Plan de Estudio', icon: '📅' },
  { key: 'has_analytics', label: 'Estadísticas', icon: '📊' },
  { key: 'has_community', label: 'Comunidad', icon: '👥' },
  { key: 'has_library', label: 'Biblioteca', icon: '📚' },
  { key: 'has_audios', label: 'Audios', icon: '🎧' },
];

// Categories for multi-access plans
const MULTI_ACCESS_CATEGORIES = [
  { id: 'enfermeria_urgencias', label: 'Enfermería de Urgencias (Todas CC.AA.)', icon: '🏥' },
  { id: 'enfermeria_atencion_primaria', label: 'Enfermería Atención Primaria (Todas CC.AA.)', icon: '🩺' },
  { id: 'policia_local', label: 'Policía Local (Todas las oposiciones)', icon: '👮' },
  { id: 'bomberos', label: 'Bomberos (Todas las oposiciones)', icon: '🚒' },
  { id: 'administrativo', label: 'Administrativo (Todas CC.AA.)', icon: '📋' },
  { id: 'auxiliar_administrativo', label: 'Auxiliar Administrativo (Todas CC.AA.)', icon: '📝' },
  { id: 'justicia', label: 'Justicia (Todos los cuerpos)', icon: '⚖️' },
  { id: 'educacion', label: 'Educación (Todas especialidades)', icon: '📚' },
];

const PlansManagement = ({ token }) => {
  const [plans, setPlans] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [syncingStripe, setSyncingStripe] = useState(false);

  const loadStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Error loading Stripe status:', error);
    }
  };

  const syncStripeProducts = async () => {
    setSyncingStripe(true);
    try {
      const response = await axios.post(`${API}/payments/stripe/sync-products`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sincronización completada: ${response.data.products_created?.length || 0} productos creados, ${response.data.prices_created?.length || 0} precios creados`);
      loadStripeStatus();
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar con Stripe');
    } finally {
      setSyncingStripe(false);
    }
  };

  const emptyPlan = {
    plan_id: '',
    name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    credits_monthly: 0,
    max_credits_limit: 0,
    features: [],
    has_tutor_ia: false,
    has_simulacro: true,
    has_quick_test: true,
    has_flashcards: false,
    has_study_plan: false,
    has_analytics: false,
    has_community: false,
    has_library: false,
    has_audios: false,
    max_exams_daily: 10,
    is_active: true,
    is_public: true,
    order: 0,
    badge_color: 'blue',
    // Multi-access fields
    is_multi_access: false,
    multi_access_type: null,
    multi_access_professions: [],
    multi_access_categories: [],
    // Discount fields
    discount_percent: null,
    discount_code: '',
    discount_expires: '',
    original_price_monthly: null,
    original_price_yearly: null,
    // Customer-facing features
    display_features_included: [],
    display_features_excluded: [],
    // Profession-specific pricing
    profession_pricing: {}
  };

  useEffect(() => {
    loadPlans();
    loadProfessions();
    loadStripeStatus();
  }, []);

  const loadProfessions = async () => {
    try {
      const res = await axios.get(`${API}/professions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfessions(res.data.professions || []);
    } catch (error) {
      console.error('Error loading professions:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/plans/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(res.data.plans || []);
    } catch (error) {
      toast.error('Error cargando planes');
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    if (!editingPlan?.plan_id || !editingPlan?.name) {
      toast.error('ID y nombre son obligatorios');
      return;
    }

    setSaving(true);
    try {
      const isNew = !plans.find(p => p.plan_id === editingPlan.plan_id);
      
      if (isNew) {
        await axios.post(`${API}/plans/admin/create`, editingPlan, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan creado');
      } else {
        await axios.put(`${API}/plans/admin/${editingPlan.plan_id}`, editingPlan, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan actualizado');
      }
      
      setEditingPlan(null);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando plan');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (feature) => {
    setEditingPlan(prev => ({ ...prev, [feature]: !prev[feature] }));
  };

  const deletePlan = async (planId) => {
    if (!confirm(`¿Estás seguro de eliminar el plan "${planId}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      // Use force=true to permanently delete
      await axios.delete(`${API}/plans/admin/${planId}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan eliminado permanentemente');
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando plan');
    }
  };

  const getPlanIcon = (planId) => {
    switch(planId) {
      case 'trial': return '🎁';
      case 'basic': return '📘';
      case 'top': return '⭐';
      case 'premium': return '👑';
      case 'institutional': return '🏛️';
      default: return '📦';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Gestión de Planes de Suscripción</h3>
        <Button onClick={() => setEditingPlan({ ...emptyPlan })} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Plan
        </Button>
      </div>

      {/* Stripe Status Card */}
      <div className={`p-4 rounded-xl border ${stripeStatus?.connected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className={`w-6 h-6 ${stripeStatus?.connected ? 'text-green-600' : 'text-amber-600'}`} />
            <div>
              <h4 className="font-medium text-slate-800">
                {stripeStatus?.connected ? '✓ Stripe Conectado' : '⚠️ Stripe No Configurado'}
              </h4>
              <p className="text-sm text-slate-600">
                {stripeStatus?.connected 
                  ? `Modo: ${stripeStatus.mode?.toUpperCase()} | ${stripeStatus.products_synced}/${stripeStatus.total_plans} planes sincronizados`
                  : 'Configura tu API key de Stripe en Configuración > API Keys para empezar a vender'
                }
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => window.open('/pricing', '_blank')}
              className="border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              <Eye className="w-4 h-4 mr-2" />
              Ver como Cliente
            </Button>
            {stripeStatus?.connected && (
              <Button 
                variant="outline" 
                onClick={syncStripeProducts}
                disabled={syncingStripe}
                className="border-green-300 text-green-700 hover:bg-green-100"
              >
                {syncingStripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Sincronizar Productos
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="space-y-4">
        {plans.map(plan => (
          <div key={plan.plan_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Plan Header */}
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedPlan(expandedPlan === plan.plan_id ? null : plan.plan_id)}
            >
              <div className="flex items-center gap-4">
                <span className="text-2xl">{getPlanIcon(plan.plan_id)}</span>
                <div>
                  <h4 className="font-medium text-slate-800">{plan.name}</h4>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-semibold text-slate-800">{plan.price_monthly}€/mes</p>
                  <p className="text-xs text-slate-500">{plan.credits_monthly?.toLocaleString()} créditos</p>
                </div>
                <div className="flex items-center gap-2">
                  {plan.is_active ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Activo</span>
                  ) : (
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Inactivo</span>
                  )}
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingPlan(plan); }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (window.confirm(`¿Eliminar plan "${plan.name}"? Esta acción no se puede deshacer.`)) {
                        deletePlan(plan.plan_id);
                      }
                    }}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedPlan === plan.plan_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedPlan === plan.plan_id && (
              <div className="p-4 bg-slate-50 border-t border-slate-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Precio Anual</p>
                    <p className="font-medium">{plan.price_yearly}€</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Límite Créditos</p>
                    <p className="font-medium">{plan.max_credits_limit?.toLocaleString() || 'Sin límite'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Exámenes/día</p>
                    <p className="font-medium">{plan.max_exams_daily}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Visible</p>
                    <p className="font-medium">{plan.is_public ? 'Sí' : 'No'}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-xs text-slate-500 mb-2">Funcionalidades</p>
                  <div className="flex flex-wrap gap-2">
                    {FEATURES_LIST.map(f => (
                      <span 
                        key={f.key}
                        className={`px-2 py-1 text-xs rounded-full ${
                          plan[f.key] 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {f.icon} {f.label}
                      </span>
                    ))}
                  </div>
                </div>
                {plan.features?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-2">Descripción de características</p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {plan.features.map((f, i) => (
                        <li key={i}>• {f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold">
                {plans.find(p => p.plan_id === editingPlan.plan_id) ? 'Editar Plan' : 'Nuevo Plan'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-600">ID del Plan*</label>
                  <Input
                    value={editingPlan.plan_id}
                    onChange={(e) => setEditingPlan({ ...editingPlan, plan_id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    disabled={plans.find(p => p.plan_id === editingPlan.plan_id)}
                    placeholder="basic, pro, etc."
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Nombre*</label>
                  <Input
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                    placeholder="Plan Básico"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-600">Descripción</label>
                <Input
                  value={editingPlan.description || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                  placeholder="Descripción corta del plan"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-slate-600">Precio Mensual (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price_monthly}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price_monthly: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Precio Anual (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price_yearly}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price_yearly: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Créditos/mes</label>
                  <Input
                    type="number"
                    value={editingPlan.credits_monthly}
                    onChange={(e) => setEditingPlan({ ...editingPlan, credits_monthly: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Límite máx.</label>
                  <Input
                    type="number"
                    value={editingPlan.max_credits_limit || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, max_credits_limit: parseInt(e.target.value) || null })}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-600 mb-2 block">Funcionalidades</label>
                <div className="grid grid-cols-3 gap-2">
                  {FEATURES_LIST.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggleFeature(f.key)}
                      className={`p-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                        editingPlan[f.key]
                          ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                          : 'bg-slate-100 text-slate-600 border-2 border-transparent'
                      }`}
                    >
                      {editingPlan[f.key] ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {f.icon} {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Multi-Access Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-3 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingPlan.is_multi_access || false}
                      onChange={(e) => setEditingPlan({ 
                        ...editingPlan, 
                        is_multi_access: e.target.checked,
                        multi_access_type: e.target.checked ? 'category' : null
                      })}
                      className="rounded border-amber-300 text-amber-600"
                    />
                    <Globe className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Multi-Acceso Premium</span>
                  </label>
                  <span className="text-xs text-slate-500">(Acceso a múltiples oposiciones)</span>
                </div>

                {editingPlan.is_multi_access && (
                  <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div>
                      <label className="text-sm text-slate-600 mb-2 block">Tipo de Multi-Acceso</label>
                      <select
                        value={editingPlan.multi_access_type || 'category'}
                        onChange={(e) => setEditingPlan({ ...editingPlan, multi_access_type: e.target.value })}
                        className="w-full p-2 border border-slate-300 rounded-lg"
                      >
                        <option value="category">Por Categoría (ej: Todas las Enfermería de Urgencias)</option>
                        <option value="all_regions">Todas las CC.AA. de una profesión</option>
                        <option value="custom">Personalizado (seleccionar profesiones)</option>
                      </select>
                    </div>

                    {editingPlan.multi_access_type === 'category' && (
                      <div>
                        <label className="text-sm text-slate-600 mb-2 block">Categorías incluidas</label>
                        <div className="grid grid-cols-2 gap-2">
                          {MULTI_ACCESS_CATEGORIES.map(cat => (
                            <label 
                              key={cat.id}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                (editingPlan.multi_access_categories || []).includes(cat.id)
                                  ? 'bg-amber-100 border-2 border-amber-400'
                                  : 'bg-white border border-slate-200 hover:border-amber-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={(editingPlan.multi_access_categories || []).includes(cat.id)}
                                onChange={(e) => {
                                  const current = editingPlan.multi_access_categories || [];
                                  if (e.target.checked) {
                                    setEditingPlan({ ...editingPlan, multi_access_categories: [...current, cat.id] });
                                  } else {
                                    setEditingPlan({ ...editingPlan, multi_access_categories: current.filter(c => c !== cat.id) });
                                  }
                                }}
                                className="hidden"
                              />
                              <span className="text-lg">{cat.icon}</span>
                              <span className="text-sm">{cat.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {editingPlan.multi_access_type === 'custom' && professions.length > 0 && (
                      <div>
                        <label className="text-sm text-slate-600 mb-2 block">
                          Profesiones incluidas ({(editingPlan.multi_access_professions || []).length} seleccionadas)
                        </label>
                        <div className="max-h-48 overflow-y-auto space-y-1 border border-slate-200 rounded-lg p-2">
                          {professions.filter(p => p.is_active !== false).map(prof => (
                            <label 
                              key={prof.profession_id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                (editingPlan.multi_access_professions || []).includes(prof.profession_id)
                                  ? 'bg-amber-100'
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={(editingPlan.multi_access_professions || []).includes(prof.profession_id)}
                                onChange={(e) => {
                                  const current = editingPlan.multi_access_professions || [];
                                  if (e.target.checked) {
                                    setEditingPlan({ ...editingPlan, multi_access_professions: [...current, prof.profession_id] });
                                  } else {
                                    setEditingPlan({ ...editingPlan, multi_access_professions: current.filter(p => p !== prof.profession_id) });
                                  }
                                }}
                                className="rounded border-amber-300 text-amber-600"
                              />
                              <span className="text-sm">{prof.name}</span>
                              {prof.region && <span className="text-xs text-slate-400">({prof.region})</span>}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-amber-700">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      Los usuarios con este plan podrán acceder al contenido de todas las oposiciones/regiones seleccionadas
                    </p>
                  </div>
                )}
              </div>

              {/* Discount Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-lg">🏷️</span>
                  <span className="text-sm font-medium text-slate-700">Descuentos y Promociones</span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <label className="text-sm text-slate-600">% Descuento</label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      placeholder="20"
                      value={editingPlan.discount_percent || ''}
                      onChange={(e) => setEditingPlan({ 
                        ...editingPlan, 
                        discount_percent: e.target.value ? parseFloat(e.target.value) : null 
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Código Promo</label>
                    <Input
                      placeholder="PROMO20"
                      value={editingPlan.discount_code || ''}
                      onChange={(e) => setEditingPlan({ ...editingPlan, discount_code: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Expira</label>
                    <Input
                      type="date"
                      value={editingPlan.discount_expires?.split('T')[0] || ''}
                      onChange={(e) => setEditingPlan({ ...editingPlan, discount_expires: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Precio original (mes)</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Para tachado"
                      value={editingPlan.original_price_monthly || ''}
                      onChange={(e) => setEditingPlan({ 
                        ...editingPlan, 
                        original_price_monthly: e.target.value ? parseFloat(e.target.value) : null 
                      })}
                    />
                  </div>
                </div>
                
                {editingPlan.discount_percent > 0 && (
                  <p className="text-xs text-green-700 mt-2">
                    ✓ Este plan mostrará un descuento del {editingPlan.discount_percent}%
                    {editingPlan.discount_code && ` con código "${editingPlan.discount_code}"`}
                    {editingPlan.discount_expires && ` hasta ${new Date(editingPlan.discount_expires).toLocaleDateString('es-ES')}`}
                  </p>
                )}
              </div>

              {/* Customer-facing Features Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-lg">👁️</span>
                  <span className="text-sm font-medium text-slate-700">Lo que ve el Cliente</span>
                </div>
                
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-700 mb-3">
                    Define qué características mostrar al usuario en la página de precios. 
                    Cada línea será un ítem de la lista. Usa ✓ o ✗ al inicio para indicar incluido/no incluido.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-slate-600">Qué INCLUYE (✓)</label>
                      <textarea
                        rows={4}
                        placeholder="✓ Acceso a todos los simulacros&#10;✓ Tests rápidos ilimitados&#10;✓ Tutor IA personal&#10;✓ Plan de estudio personalizado"
                        value={(editingPlan.display_features_included || []).join('\n')}
                        onChange={(e) => setEditingPlan({ 
                          ...editingPlan, 
                          display_features_included: e.target.value.split('\n').filter(l => l.trim()) 
                        })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-slate-600">Qué NO incluye (✗)</label>
                      <textarea
                        rows={3}
                        placeholder="✗ Biblioteca de audios&#10;✗ Comunidad premium&#10;✗ Estadísticas avanzadas"
                        value={(editingPlan.display_features_excluded || []).join('\n')}
                        onChange={(e) => setEditingPlan({ 
                          ...editingPlan, 
                          display_features_excluded: e.target.value.split('\n').filter(l => l.trim()) 
                        })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Profession-specific Pricing Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-lg">💰</span>
                  <span className="text-sm font-medium text-slate-700">Precios por Oposición</span>
                  <span className="text-xs text-slate-500">(Opcional - Si no se especifica, usa precio base)</span>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700 mb-3">
                    Define precios diferentes para cada oposición. Deja vacío para usar el precio base del plan.
                  </p>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {professions.filter(p => p.is_active !== false).map(prof => {
                      const profPricing = editingPlan.profession_pricing?.[prof.profession_id] || {};
                      return (
                        <div key={prof.profession_id} className="flex items-center gap-4 p-2 bg-white rounded border border-slate-200">
                          <span className="text-sm text-slate-700 flex-1 truncate">{prof.name}</span>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="€/mes"
                              value={profPricing.monthly || ''}
                              onChange={(e) => {
                                const newPricing = { ...editingPlan.profession_pricing };
                                if (!newPricing[prof.profession_id]) {
                                  newPricing[prof.profession_id] = {};
                                }
                                newPricing[prof.profession_id].monthly = e.target.value ? parseFloat(e.target.value) : null;
                                if (!newPricing[prof.profession_id].monthly && !newPricing[prof.profession_id].yearly) {
                                  delete newPricing[prof.profession_id];
                                }
                                setEditingPlan({ ...editingPlan, profession_pricing: newPricing });
                              }}
                              className="w-24 h-8 text-sm"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="€/año"
                              value={profPricing.yearly || ''}
                              onChange={(e) => {
                                const newPricing = { ...editingPlan.profession_pricing };
                                if (!newPricing[prof.profession_id]) {
                                  newPricing[prof.profession_id] = {};
                                }
                                newPricing[prof.profession_id].yearly = e.target.value ? parseFloat(e.target.value) : null;
                                if (!newPricing[prof.profession_id].monthly && !newPricing[prof.profession_id].yearly) {
                                  delete newPricing[prof.profession_id];
                                }
                                setEditingPlan({ ...editingPlan, profession_pricing: newPricing });
                              }}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {Object.keys(editingPlan.profession_pricing || {}).length > 0 && (
                    <p className="text-xs text-blue-700 mt-2">
                      ✓ {Object.keys(editingPlan.profession_pricing).length} oposiciones con precio personalizado
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-slate-600">Exámenes/día</label>
                  <Input
                    type="number"
                    value={editingPlan.max_exams_daily}
                    onChange={(e) => setEditingPlan({ ...editingPlan, max_exams_daily: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Orden</label>
                  <Input
                    type="number"
                    value={editingPlan.order}
                    onChange={(e) => setEditingPlan({ ...editingPlan, order: parseInt(e.target.value) })}
                  />
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingPlan.is_active}
                      onChange={(e) => setEditingPlan({ ...editingPlan, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Activo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingPlan.is_public}
                      onChange={(e) => setEditingPlan({ ...editingPlan, is_public: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Público</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditingPlan(null)}>Cancelar</Button>
              <Button onClick={savePlan} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansManagement;
